import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ImageField, RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import SiteNameRichText from 'components/SiteNameRichText/SiteNameRichText';
import ButtonDiscreet from 'components/shared/Button/ButtonDiscreet';
import ButtonPrimary from 'components/shared/Button/PrimaryButton';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { withLocationParam } from 'lib/utils/locationSearchParam';
import { showLoader } from 'src/features/loaderSlice';
import {
  StButtonWrap,
  StCardHeader,
  StClosestBadge,
  StClosestBadgeText,
  StConditionTag,
  StConditionTagText,
  StInactiveButtonWrap,
  StLocationBullet,
  StLocationPinWrap,
  StLocationRow,
  StLocationText,
  StSiteCard,
  StSiteInfo,
  StSiteTitle,
  StStatColumn,
  StStatLabel,
  StStatNumber,
  StStatsRow,
  StTagsRow,
} from './mapCoveoResultToCard.styles';
import { SiteCard } from './mapCoveoResultToCard.types';
type LocationSearchSiteCardProps = {
  site: SiteCard;
  isClosest: boolean;
  isSelected: boolean;
  closestLabel?: string;
  activeStudiesLabel?: string;
  recruitingNowLabel?: string;
  conditionsLabel?: string;
  viewSiteLabel?: string;
  notActivelyRecruitingLabel?: string;
  cardLocationIcon?: ImageField;
  siteNameFormat?: RichTextField;
  hasLocationContext?: boolean;
  searchLocality?: string;
  onCardMount?: (element: HTMLDivElement | null) => void;
  onSelect?: (siteId: string) => void;
};
const DEFAULT_VISIBLE_CONDITIONS = 3;
const INLINE_DISCREET_BUTTON_STYLE = {
  minHeight: 'auto',
  height: 'auto',
  alignSelf: 'center',
  fontSize: '14px',
  fontWeight: 500,
  padding: '0 8px',
};

function LocationSearchSiteCard({
  site,
  isClosest,
  isSelected,
  closestLabel,
  activeStudiesLabel,
  recruitingNowLabel,
  conditionsLabel,
  viewSiteLabel,
  notActivelyRecruitingLabel = 'Not actively recruiting',
  cardLocationIcon,
  siteNameFormat,
  hasLocationContext = false,
  searchLocality,
  onCardMount,
  onSelect,
}: LocationSearchSiteCardProps): JSX.Element {
  const dispatch = useDispatch();
  const siteHref = withLocationParam(site.url, searchLocality);
  const locationParts = [site.city, site.state].filter(Boolean);
  const locationLine = locationParts.join(', ');
  const hasDistance = hasLocationContext && typeof site.distanceMiles === 'number';
  const cardIconSrc = cardLocationIcon?.value?.src;
  const [conditionsExpanded, setConditionsExpanded] = useState(false);
  const conditions = site.relatedConditions;
  const hiddenCount = Math.max(0, conditions.length - DEFAULT_VISIBLE_CONDITIONS);
  const visibleConditions = conditionsExpanded
    ? conditions
    : conditions.slice(0, DEFAULT_VISIBLE_CONDITIONS);
  const showConditionsToggle = hiddenCount > 0;
  const ButtonWrap = site.isRecruitingActive ? StButtonWrap : StInactiveButtonWrap;
  const siteInfoForName = {
    siteName: site.name,
    siteCity: site.city ?? '',
    siteState: site.state ?? '',
    latitude: site.latitude ?? 0,
    longitude: site.longitude ?? 0,
    isHybridSite: false,
  };

  const handleCardSelect = () => {
    onSelect?.(site.id);
  };

  const isInteractiveTarget = (target: EventTarget | null): boolean =>
    Boolean((target as HTMLElement | null)?.closest?.('a, button'));

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (!onSelect || isInteractiveTarget(event.target)) {
      return;
    }
    handleCardSelect();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onSelect || isInteractiveTarget(event.target)) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleCardSelect();
  };

  const cardSelectProps = onSelect
    ? {
        tabIndex: 0,
        'aria-label': `Select ${site.name} on map`,
        'aria-selected': isSelected,
        onClick: handleCardClick,
        onKeyDown: handleCardKeyDown,
      }
    : { tabIndex: -1 };

  return (
    <StSiteCard
      ref={onCardMount}
      id={`site-card-${site.id}`}
      selected={isSelected}
      {...cardSelectProps}
    >
      <StCardHeader>
        <StSiteInfo>
          {siteNameFormat?.value ? (
            <StSiteTitle>
              <SiteNameRichText siteNameFormat={siteNameFormat} siteInfo={siteInfoForName} />
            </StSiteTitle>
          ) : (
            <StSiteTitle>{site.name}</StSiteTitle>
          )}

          {locationLine && (
            <StLocationRow>
              {cardIconSrc && (
                <StLocationPinWrap>
                  <NextImage
                    src={cardIconSrc}
                    width="16"
                    height="16"
                    alt={`${cardLocationIcon?.value?.alt}`}
                    aria-hidden
                  />
                </StLocationPinWrap>
              )}
              {locationLine && <StLocationText>{locationLine}</StLocationText>}
              {hasDistance && (
                <>
                  <StLocationBullet>•</StLocationBullet>
                  <StLocationText>{site.distanceMiles!.toFixed(1)} mi</StLocationText>
                </>
              )}
            </StLocationRow>
          )}
        </StSiteInfo>
        {isClosest && (
          <StClosestBadge>
            <StClosestBadgeText>{closestLabel}</StClosestBadgeText>
          </StClosestBadge>
        )}
      </StCardHeader>
      {site.isRecruitingActive ? (
        conditions.length > 0 && (
          <StTagsRow>
            {visibleConditions.map((condition, index) => (
              <StConditionTag key={`${condition}-${index}`}>
                <StConditionTagText>{condition}</StConditionTagText>
              </StConditionTag>
            ))}

            {showConditionsToggle && (
              <ButtonDiscreet
                type="button"
                aria-expanded={conditionsExpanded}
                aria-label={conditionsExpanded ? 'Show less' : `+${hiddenCount} more`}
                style={INLINE_DISCREET_BUTTON_STYLE}
                onClick={() => {
                  setConditionsExpanded((prev) => !prev);
                }}
              >
                {conditionsExpanded ? 'Show less' : `+${hiddenCount} more`}
              </ButtonDiscreet>
            )}
          </StTagsRow>
        )
      ) : (
        <StTagsRow>
          <StConditionTag>
            <StConditionTagText>{notActivelyRecruitingLabel}</StConditionTagText>
          </StConditionTag>
        </StTagsRow>
      )}

      {site.isRecruitingActive && (
        <StStatsRow>
          <StStatColumn>
            <StStatNumber>{site.activeStudiesCount ?? 0}</StStatNumber>
            <StStatLabel>{activeStudiesLabel}</StStatLabel>
          </StStatColumn>
          <StStatColumn>
            <StStatNumber>{site.recruitingNowCount ?? 0}</StStatNumber>
            <StStatLabel>{recruitingNowLabel}</StStatLabel>
          </StStatColumn>
          <StStatColumn>
            <StStatNumber>{site.conditionsCount ?? 0}</StStatNumber>
            <StStatLabel>{conditionsLabel}</StStatLabel>
          </StStatColumn>
        </StStatsRow>
      )}
      <ButtonWrap>
        {site.isRecruitingActive ? (
          <ButtonPrimary
            href={siteHref}
            component={NextLink}
            fullWidth
            onClick={() => {
              dispatch(showLoader());
            }}
          >
            {viewSiteLabel}
          </ButtonPrimary>
        ) : (
          <ButtonSecondary
            href={siteHref}
            component={NextLink}
            fullWidth
            onClick={() => {
              dispatch(showLoader());
            }}
          >
            {viewSiteLabel}
          </ButtonSecondary>
        )}
      </ButtonWrap>
    </StSiteCard>
  );
}

export default LocationSearchSiteCard;
